// externals
import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import defaults from 'lodash.defaults';

// libraries
import type { StateTree } from '@makemydeal/dr-dash-types';

// utils
import { convertToNumber, isNaNish } from './formatUtils';
import { useDebounce } from './useDebounce';
import { DEBOUNCE_TIMER } from '../constants';
import { isBlurEvent } from '@makemydeal/dr-shared-ui-utils';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as paymentSelectors from '@makemydeal/dr-dash-store/selectors/payment';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useHeldValue } from '../hooks/useHeldSelector';

// optional props
type UsePaymentCallOptionsParam = {
    formatValueToString?: (value: any) => string;
    parseValue?: (text: any) => any; // parse text to the value used in the underlying action creator. i.e. number | string, etc
    ignoreTextValue?: (text: string) => boolean;
    autoSave?: boolean;
    // Runs when an edit is applied to the deal, and only when the user typed it — not when the app sets
    // the same value on its own. Ex: ExcessMileageChargeField, which takes its handlers from this hook,
    // so only the hook sees the raw change event and can tell those apart.
    onUserEdit?: () => void;
    holdWhileTermResponsePending?: boolean;
};

type UsePaymentCallOptions = {
    formatValueToString: (value: any) => string;
    parseValue: (text: string) => any; // parse text to the value used in the underlying action creator. i.e. number | string, etc
    ignoreTextValue: (text: string) => boolean;
    autoSave: boolean;
    onUserEdit?: () => void;
    holdWhileTermResponsePending?: boolean;
};

type PaymentInputEvent = (e: any) => void;

type PaymentCallResult = {
    value: string;
    handleChange: PaymentInputEvent;
    handleBlur: PaymentInputEvent;
    cancelChange: () => void;
    handleKeyDown: PaymentInputEvent;
};

/**
 * This is a custom hook used for sharing the logic of making payment calls based on textbox input changes
 * @param selector The selector that provides the initial value for the input
 * @param actionCreator The wrapped action creator
 * @param options Optional arguments: toString: (any) => string, toValue: (string) => any
 * @returns A tuple containing the current component state value and a change handler for this value
 */
export const usePaymentCall = (
    selector: (state: StateTree) => any,
    actionCreator: (...args: any[]) => any,
    options: UsePaymentCallOptionsParam = {}
): PaymentCallResult => {
    // null check and set any missing props
    defaults(options, {
        formatValueToString: (v: any) => `${v}`,
        parseValue: convertToNumber,
        autoSave: true,
        ignoreTextValue: isNaNish // assuming numerics; we'll ignore any text that has no numbers.
    });

    // type casting to prevent compiler undefined check warnings.
    const _options = options as UsePaymentCallOptions;

    // destructuring for legibility
    const { formatValueToString, parseValue, ignoreTextValue, autoSave, onUserEdit, holdWhileTermResponsePending } = _options;

    // get the initial field value
    const liveSelectorValue = useSelector(selector);
    const selectorValue = useHeldValue(liveSelectorValue, undefined, holdWhileTermResponsePending === true);
    const isCalculatingPayment = useSelector(paymentSelectors.isCalculatingPayment);
    const wasCalculatingPayment = useRef(isCalculatingPayment);

    // DE759192: escape hatch for the removal of the premature isPaymentCallPending clear below. ON skips
    // the clear (fix); OFF preserves today's behavior so it can be flipped back in seconds if needed.
    const isSkipPrematurePaymentPendingClearEnabled = useSelector(featureToggleSelectors.isSkipPrematurePaymentPendingClearEnabled);

    // converted value to watch/store at the component level.
    const [value, setValue] = React.useState(selectorValue);
    const prevOnBlurValue = useRef(value);
    const userTyped = useRef(false);

    // Store the value in the text box for input control
    const [textValue, setTextValue] = React.useState(formatValueToString(selectorValue));

    // wrap the action creator with dispatch
    const dispatch = useDispatch();

    const dispatchAction = (...args: any[]) => dispatch(actionCreator(...args));

    // ensure the payment call is not mdade on every keystroke
    const [debouncedActionCreator, cancelChange] = useDebounce(dispatchAction, DEBOUNCE_TIMER, [actionCreator]);
    // ensure we kill any executions of payment calls when unmounting the component

    // DOM event delegates for the input
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isBlurEvent(e)) {
            return;
        }
        userTyped.current = true;
        setTextValue(e.target.value);
    };

    const notifyIfUserEdited = () => {
        if (!userTyped.current) return;
        userTyped.current = false;
        onUserEdit?.();
    };

    const handleBlur = () => {
        // Parse the current text value to ensure we're working with the latest input
        const latestValue = parseValue(textValue);
        const formatted = formatValueToString(latestValue);
        setTextValue(formatted);
        if (autoSave) {
            if (prevOnBlurValue.current !== latestValue) {
                prevOnBlurValue.current = latestValue;
                dispatchAction(latestValue);
                if (!isSkipPrematurePaymentPendingClearEnabled) {
                    dispatch(offerActionCreators.updatePaymentCallPendingState(false));
                }
                notifyIfUserEdited();
            }
        }
    };
    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const latestValue = parseValue(textValue);
        if (autoSave && event.key === 'Enter' && prevOnBlurValue.current !== latestValue) {
            prevOnBlurValue.current = latestValue;
            dispatchAction(latestValue);
            if (!isSkipPrematurePaymentPendingClearEnabled) {
                dispatch(offerActionCreators.updatePaymentCallPendingState(false));
            }
            notifyIfUserEdited();
        }
    };

    const syncFromSelectorValue = () => {
        userTyped.current = false;
        setValue(selectorValue); // set to same value; to prevent override dispatch
        setTextValue(formatValueToString(selectorValue)); // will update text without dispatching action
        if (autoSave) {
            prevOnBlurValue.current = selectorValue;
        }
    };

    // NOTE: pass down store changes to update; for legibility, using long-form function.
    useEffect(() => {
        return () => cancelChange();
    }, []); // construct/destruct

    // NOTE: we're watching the text input of the component and updating the converted value. If valid, we're trigging upstream changes in redux
    useEffect(() => {
        const newValue = parseValue(textValue); // brute force strips non-numerics and then convert. ($-- == 0)
        setValue(newValue);

        if (newValue === value) return; // if local state matches the newly converted value, ignore this
        if (autoSave && !ignoreTextValue(textValue)) {
            dispatch(offerActionCreators.updatePaymentCallPendingState(prevOnBlurValue.current !== newValue));
            return;
        }
        if (!ignoreTextValue(textValue)) {
            debouncedActionCreator(newValue);
        } else {
            cancelChange();
        }
    }, [textValue]);

    useEffect(() => {
        if (selectorValue !== value) {
            syncFromSelectorValue();
        }
    }, [selectorValue]);

    useEffect(() => {
        const wasCalculating = wasCalculatingPayment.current;
        wasCalculatingPayment.current = isCalculatingPayment;

        // When payment calculation completes, re-sync UI to selectorValue even if selectorValue didn't change.
        if (wasCalculating && !isCalculatingPayment) {
            syncFromSelectorValue();
        }
    }, [isCalculatingPayment, selectorValue]);

    return {
        value: textValue,
        handleChange,
        handleBlur,
        cancelChange,
        handleKeyDown
    };
};
